import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import React, { useMemo, useState } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { safeDivide } from 'src/utils/app';
import useAtlasV2DorisColumnData from 'src/serverProxy/useAtlasV2DorisColumnData';
import { DorisDropdownFieldNameEnum } from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { useDorisEntityDropdownOptions } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficDoubleDropdownTopEntity';
import SearchableDropdown from 'src/components/AtlasV2Components/Common/SearchableDropdown';
import { titleCase } from 'src/utils/stringFormatting';

const fieldOptions = [
  {
    label: 'Market Share of Units Sold',
    id: 'unitsSold',
    fieldName: 'unitsSold',
    marketMetric: 'unitsSold'
  },
  {
    label: '1P Market Share of Units Sold',
    id: 'firstPartyUnitsSold/unitsSold',
    fieldName: 'firstPartyUnitsSold',
    marketMetric: 'unitsSold'
  },
  {
    label: '1P Market Share of 1P Units Sold',
    id: 'firstPartyUnitsSold/onePUnitsSold',
    fieldName: 'firstPartyUnitsSold',
    marketMetric: 'firstPartyUnitsSold'
  },
  {
    label: '3P Market Share of Units Sold',
    id: 'ThirdPartyUnitsSold',
    fieldName: 'ThirdPartyUnitsSold',
    marketMetric: 'unitsSold'
  },
  {
    label: '3P Market Share of 3P Units Sold',
    id: 'ThirdPartyUnitsSold/ThirdPartyUnitsSold',
    fieldName: 'ThirdPartyUnitsSold',
    marketMetric: 'ThirdPartyUnitsSold'
  }
];

interface AtlasUnitsSoldMarketShareColumnChartProps {
  enableSearchableGroupBy?: boolean;
}

/**
 *
 * Units Sold Market Share Column Chart
 */
const AtlasUnitsSoldMarketShareColumnChart = ({
  enableSearchableGroupBy
}: AtlasUnitsSoldMarketShareColumnChartProps) => {
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const groupByOptions = useDorisEntityDropdownOptions(DorisDropdownFieldNameEnum.Sales);

  const [selectedMainMetricField, setSelectedMainMetricField] = useState('unitsSold');

  const [selectedGroupByFieldId, setSelectedGroupByFieldId] = useState('brand');
  const [selectedCustomTag, setSelectedCustomTag] = useState<string | null>(null);

  const isCustomMode = enableSearchableGroupBy && selectedGroupByFieldId === 'productAttributeValue';

  const currentGroupByOption = useMemo(() => {
    if (isCustomMode) {
      return {
        id: 'productAttributeValue',
        label: titleCase(selectedCustomTag || 'Product Attribute'),
        indexName: 'sales'
      } as unknown as { id: string; label: string; indexName?: string };
    }
    return groupByOptions.find((option) => option.id === selectedGroupByFieldId) || groupByOptions[0];
  }, [groupByOptions, isCustomMode, selectedCustomTag, selectedGroupByFieldId]);

  const currentMetricOption = fieldOptions.find((option) => option.id === selectedMainMetricField);

  const { data: targetData, isLoading: isTargetLoading } = useAtlasV2DorisColumnData({
    metricType: currentMetricOption.fieldName,
    fieldName: selectedGroupByFieldId,
    queryKey: 'Atlas-unitsSold-target',
    endPoint: 'sales/GetSalesMetrics',
    additionalParams: isCustomMode && selectedCustomTag ? { productTags: [selectedCustomTag] } : undefined
  });

  const { data: marketShareData, isLoading: isMarketShareLoading } = useAtlasV2DorisColumnData({
    metricType: currentMetricOption.marketMetric,
    fieldName: 'retailer',
    queryKey: 'Atlas-unitsSold-market',
    endPoint: 'sales/GetSalesMetrics'
  });

  const parsedData = useMemo(() => {
    const comparisonData = [];
    const currentData = [];
    const categoryData = [];

    if (!isMarketShareLoading && !isTargetLoading) {
      const currentMarketShare = marketShareData?.[0]?.values?.retailer;
      const compareMarketShare = marketShareData?.[0]?.comparisonValues?.retailer;

      targetData.forEach((datum) => {
        const datumName = datum?.metadata?.name;

        const formattedCategoryObj = {
          name: datumName,
          id: datum.fieldId,
          type: currentGroupByOption?.id
        };

        const currentDatum = datum?.values?.[currentGroupByOption?.id] ?? 0;
        const comparisonDatum = datum?.comparisonValues?.[currentGroupByOption?.id] ?? 0;

        comparisonData.push([datumName, safeDivide(comparisonDatum, compareMarketShare)]);
        currentData.push([datumName, safeDivide(currentDatum, currentMarketShare)]);
        categoryData.push(formattedCategoryObj);
      });
    }

    return { currentData, categoryData, comparisonData };
  }, [currentGroupByOption, isMarketShareLoading, isTargetLoading, marketShareData, targetData]);

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        const label = `Category Market Share by ${currentGroupByOption.label}`;

        return saveBarChartAsDelimitedData(parsedData.currentData, parsedData.comparisonData, {
          fileTitle: `Units Sold - ${label}`,
          metricLabel: label
        });
      }}
      includeSummaryInfo
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
      loading={isMarketShareLoading || isTargetLoading}
      loadingComponent={<TopEntityChartV2Loading enableSubTitleSkeleton />}
      title={
        <Flex alignItems="center">
          <SlDropdownMenu
            buttonVariant="title"
            options={fieldOptions}
            selectedId={selectedMainMetricField}
            onChange={(option) => {
              setSelectedMainMetricField(option.id);
            }}
            defaultLabel="Market Share of Units Sold"
          />
          <Text variant="h4" sx={{ margin: '0 8px' }}>
            by
          </Text>
          {enableSearchableGroupBy ? (
            <SearchableDropdown
              buttonVariant="title"
              standardOptions={groupByOptions as any}
              selectedId={selectedGroupByFieldId}
              selectedCustomTag={selectedCustomTag || undefined}
              onChange={(opt) => {
                setSelectedCustomTag(null);
                setSelectedGroupByFieldId(opt.id);
              }}
              onCustomSelect={(tag) => {
                setSelectedCustomTag(tag);
                setSelectedGroupByFieldId('productAttributeValue');
              }}
              defaultLabel="Brand"
            />
          ) : (
            <SlDropdownMenu
              buttonVariant="title"
              options={groupByOptions}
              selectedId={selectedGroupByFieldId}
              onChange={(option) => setSelectedGroupByFieldId(option.id)}
              defaultLabel="Brand"
            />
          )}
        </Flex>
      }
    >
      <TopEntityChartV2
        chartSeries={[
          {
            type: 'column',
            data: parsedData.comparisonData,
            categories: parsedData.categoryData,
            metricType: METRICTYPE.PERCENT
          },
          {
            type: 'column',
            data: parsedData.currentData,
            categories: parsedData.categoryData,
            metricType: METRICTYPE.PERCENT
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default AtlasUnitsSoldMarketShareColumnChart;
